import { Divider } from "@msuite/ui/divider";
import { FormCheckbox, Textarea } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { hasNoteContent } from "@msuite/utils/notes";
import { type PostNoteForm, postNoteSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import type { NoteWithFiles } from "@msuite/utils/types";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";
import { useMentionOptions } from "@/hooks/use-mention-options";

/** Props Interface */
interface EditNoteModalProps {
	isOpen: boolean;
	onClose: () => void;
	onComplete: () => void | Promise<void>;
	note: NoteWithFiles;
	withIsImportant?: boolean;
	isImportantLabel?: string;
	withIsImportantIsDisabled?: boolean;
}

export function EditNoteModal({
	isOpen,
	onClose,
	note,
	onComplete,
	withIsImportant,
	isImportantLabel,
	withIsImportantIsDisabled = false,
}: EditNoteModalProps) {
	/** Hooks */
	const mentionOptions = useMentionOptions();
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(postNoteSchema),
		defaultValues: {
			note: note.note,
			noteId: note.id,
			parentNoteId: note.parentNoteId || undefined,
			isImportant: note.isImportant || false,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: PostNoteForm) {
		await client.notes[":noteId"].$patch({
			param: {
				noteId: note.id,
			},
			json,
		});
		await onComplete?.();
		onClose();
	}

	/** Computed */
	// Clearing the text is allowed where the note has attachments and refused where it has none —
	// the same rule writing follows, so a correction cannot empty a note a write could not have
	// created. This modal carries no uploader of its own, so the count is the note's.
	const canSubmit = hasNoteContent({
		note: methods.watch("note"),
		fileCount: note._files?.length ?? 0,
	});

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("notes.edit_note")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				width="50rem"
			>
				<Modal.Body>
					{/**
					  // TODO: Implement File Upload while editing
					  */}
					<Textarea
						control={methods.control}
						name="note"
						label={t("notes.note")}
						isRequired={false}
						className="max-h-[100px]"
						placeholder={t("placeholders.add_note")}
						{...mentionOptions}
					/>
					{withIsImportant && (
						<React.Fragment>
							<Divider />
							<FormCheckbox
								control={methods.control}
								name="isImportant"
								isDisabled={withIsImportantIsDisabled}
							>
								{guardString(isImportantLabel)}
							</FormCheckbox>
						</React.Fragment>
					)}
				</Modal.Body>
				<Modal.FooterForm isDisabled={!canSubmit} />
			</Modal>
		</FormProvider>
	);
}
